/**
 * VedangJyotisham — Client Invitation Service SDK
 * Decision ID: VJ-PRODUCT-INVITATION-ONLY-001
 * Specification: docs/VJ_PRODUCT_INVITATION_ONLY_001_SPEC.md (v2.1.0-final)
 */

import {
  InvitationRequestPayload,
  VerifyEmailOtpPayload,
  ValidateTokenResponse,
  FirestoreInvitationRequestDoc,
  AllocatedTier,
} from '../types/invitation';

const API_BASE = '/api';

/**
 * Helper to get authentication token from local storage
 */
function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('astroveda_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const invitationService = {
  /**
   * Submit Step 1 of Invitation Request (Name, Email, Usage, Statement)
   * Dispatches 6-digit email verification OTP
   */
  async submitRequest(payload: InvitationRequestPayload): Promise<{
    success: boolean;
    requestId?: string;
    message?: string;
    cooldownSeconds?: number;
    error?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/invitations/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        return {
          success: false,
          error: data.error || 'Failed to submit invitation request',
          cooldownSeconds: data.cooldownSeconds,
        };
      }
      return {
        success: true,
        requestId: data.requestId,
        message: data.message,
      };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error submitting request' };
    }
  },

  /**
   * Verify Email Ownership OTP (Step 1 Complete -> Transitions to EMAIL_VERIFIED)
   */
  async verifyEmailOtp(payload: VerifyEmailOtpPayload): Promise<{
    success: boolean;
    message?: string;
    remainingAttempts?: number;
    error?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/invitations/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        return {
          success: false,
          error: data.error || 'Verification failed',
          remainingAttempts: data.remainingAttempts,
        };
      }
      return {
        success: true,
        message: data.message,
      };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error verifying code' };
    }
  },

  /**
   * Validate an invitation token string before rendering redemption UI
   */
  async validateToken(token: string): Promise<ValidateTokenResponse> {
    try {
      const res = await fetch(`${API_BASE}/invitations/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) {
        return {
          valid: false,
          reason: data.error || data.reason || 'Invalid or expired invitation token',
        };
      }
      return {
        valid: data.valid ?? true,
        boundEmail: data.boundEmail,
        allocatedTier: data.allocatedTier,
        expiresAt: data.expiresAt,
        reason: data.reason,
      };
    } catch (err: any) {
      return {
        valid: false,
        reason: err.message || 'Network error validating token',
      };
    }
  },

  /**
   * Two-Phase Atomic Redemption
   */
  async redeemInvitation(
    token: string,
    registrationData: {
      name: string;
      email: string;
      password: string;
      mobile?: string;
    }
  ): Promise<{
    success: boolean;
    user?: any;
    token?: string;
    message?: string;
    error?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/invitations/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...registrationData }),
      });
      const data = await res.json();
      if (!res.ok) {
        return {
          success: false,
          error: data.error || 'Failed to redeem invitation',
        };
      }
      return {
        success: true,
        user: data.user,
        token: data.token,
        message: data.message,
      };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error during redemption' };
    }
  },

  /**
   * Verify uninvited Google OAuth user token or link invitation
   */
  async verifyOAuthInvitation(
    idToken: string,
    inviteToken?: string
  ): Promise<{
    allowed: boolean;
    user?: any;
    token?: string;
    error?: string;
    code?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/auth/oauth-invitation-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, inviteToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        return {
          allowed: false,
          error: data.error || 'OAuth invitation verification failed',
          code: data.code,
        };
      }
      return {
        allowed: true,
        user: data.user,
        token: data.token,
      };
    } catch (err: any) {
      return {
        allowed: false,
        error: err.message || 'Network error verifying OAuth session',
      };
    }
  },

  // -------------------------------------------------------------
  // SuperAdmin Operations (Requires SuperAdmin Bearer Session)
  // -------------------------------------------------------------

  /**
   * SuperAdmin: List invitation requests
   */
  async getAdminRequests(filter?: string): Promise<{
    success: boolean;
    requests: FirestoreInvitationRequestDoc[];
    error?: string;
  }> {
    try {
      const query = filter ? `?status=${encodeURIComponent(filter)}` : '';
      const res = await fetch(`${API_BASE}/admin/invitations${query}`, {
        method: 'GET',
        headers: {
          ...getAuthHeader(),
        },
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, requests: [], error: data.error || 'Failed to fetch requests' };
      }
      return { success: true, requests: data.requests || [] };
    } catch (err: any) {
      return { success: false, requests: [], error: err.message };
    }
  },

  /**
   * SuperAdmin: Approve request and allocate account tier
   */
  async approveRequest(
    requestId: string,
    allocatedTier: AllocatedTier
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/invitations/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ requestId, allocatedTier }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to approve request' };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /**
   * SuperAdmin: Decline request
   */
  async declineRequest(
    requestId: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/invitations/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ requestId, reason }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to decline request' };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /**
   * SuperAdmin: Generate and issue invitation cryptographic token
   */
  async issueInvitation(
    requestId: string,
    allocatedTier?: AllocatedTier
  ): Promise<{
    success: boolean;
    token?: string;
    invitationUrl?: string;
    error?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/admin/invitations/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ requestId, allocatedTier }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to issue token' };
      return {
        success: true,
        token: data.token,
        invitationUrl: data.invitationUrl,
      };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /**
   * SuperAdmin: Dispatch invitation email directly to applicant
   */
  async sendInvitationEmail(requestId: string): Promise<{
    success: boolean;
    deliveryStatus?: string;
    error?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/admin/invitations/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ requestId }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to send email' };
      return {
        success: true,
        deliveryStatus: data.deliveryStatus,
      };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /**
   * SuperAdmin: Resend invitation email
   */
  async resendInvitationEmail(requestId: string): Promise<{
    success: boolean;
    deliveryStatus?: string;
    error?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/admin/invitations/resend-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ requestId }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to resend email' };
      return {
        success: true,
        deliveryStatus: data.deliveryStatus,
      };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /**
   * SuperAdmin: Revoke active invitation
   */
  async revokeInvitation(tokenHashOrRequestId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const res = await fetch(`${API_BASE}/admin/invitations/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ id: tokenHashOrRequestId }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to revoke invitation' };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },
};

